import { Box, Button, Text } from "@origin-digital/ods-core";
import { IconChevronDownSmall } from "@origin-digital/ods-icons";
import {
  readDataFromStorage,
  useUserContext,
} from "@origin-digital/user-context-provider";
import React, { useState } from "react";
import styled from "styled-components";
import { IPropertySelection } from "@origin-digital/event-dispatcher";
import { BackendCrm } from "@origin-digital/platform-enums";
import { PropertySheet } from "./PropertySheet";
import { trackPropertySelectorOpen } from "./analytics";
import { content } from "./content";

const PropertySelectorContainer = styled(Box)`
  overflow: hidden;
  max-width: 100%;
  margin-left: -${({ theme }) => theme.space.xsmall}px;

  && .MuiButton-endIcon svg {
    font-size: ${({ theme }) => theme.shape.iconSizes.xxxsmall};
  }

  button {
    max-width: 100%;
  }
`;

const TextWrapper = styled.span`
  overflow: hidden;
`;

export interface IPropertySelectorProps {
  showAllProperties?: boolean;
  showServiceSelector?: boolean;
}

export const PropertySelector: React.FC<IPropertySelectorProps> = ({
  showAllProperties = true,
  showServiceSelector = false,
}) => {
  const [showSheet, setShowSheet] = useState<boolean>(false);
  const { properties, property, setProperty } = useUserContext();

  if (!properties) {
    return null;
  }

  const activeProperties = properties.filter((prop) => !prop.isPrevious);
  const inactiveProperties = properties.filter((prop) => prop.isPrevious);
  const showPropertySelector = shouldShowPropertySelector(
    properties,
    showAllProperties
  );

  // if all-properties isn't valid but is selected, then select the first property
  if (!showAllProperties && property === null) {
    const firstProperty = findDefaultProperty(properties);
    if (firstProperty) {
      setProperty(firstProperty, false, false);
    }
  }

  // if selector isn't visible and all-properties is valid, then select all
  if (!showPropertySelector && showAllProperties && property) {
    setProperty(null, false, false);
  }

  return showPropertySelector ? (
    <PropertySelectorContainer>
      <Button
        aria-label="Property Selector Button"
        variant="text"
        onClick={() => {
          setShowSheet(true);
        }}
        // Override default tracking to avoid leaking PII
        onClickCapture={() => {
          trackPropertySelectorOpen(properties?.length ?? 0);
        }}
        color="secondaryB"
        size="small"
        data-id="property-selector"
        icon={<IconChevronDownSmall color="neutral" variant="solid" />}
        iconPosition="trailing"
      >
        <TextWrapper>
          <Text
            align="left"
            variant="body-small"
            weight="medium"
            tone="neutral"
            inline={true}
            truncate
          >
            {property?.label.toUpperCase() ?? content.allPropertiesLabel}
          </Text>
        </TextWrapper>
      </Button>
      <PropertySheet
        selectedProperty={property}
        setProperty={(value) => setProperty(value, true, showServiceSelector)}
        activeProperties={activeProperties}
        inactiveProperties={inactiveProperties}
        showAllProperties={showAllProperties}
        open={showSheet}
        setOpen={setShowSheet}
      />
    </PropertySelectorContainer>
  ) : null;
};

/**
 * Find first property in this order of precedence...
 * 1. property matching stored selection
 * 2. active property with Kraken service
 * 3. active property
 * 4. inactive property with Kraken service
 * 5. inactive property
 */
export const findDefaultProperty = (properties?: IPropertySelection[]) => {
  if (!properties) {
    return undefined;
  }
  const storage = readDataFromStorage();
  const sortFunction = (a: IPropertySelection, b: IPropertySelection) => {
    if (
      storage?.selectedProperty &&
      (a.id === storage.selectedProperty) !==
        (b.id === storage.selectedProperty)
    ) {
      return a.id === storage.selectedProperty ? -1 : 1;
    }
    if (a.isPrevious !== b.isPrevious) {
      return a.isPrevious ? 1 : -1;
    }
    const aFirstAccount = a.accountServices[0];
    const bFirstAccount = b.accountServices[0];
    return (
      Number(aFirstAccount.backendType !== BackendCrm.KRAKEN) -
      Number(bFirstAccount.backendType !== BackendCrm.KRAKEN)
    );
  };
  return [...properties].sort(sortFunction)[0];
};

const shouldShowPropertySelector = (
  properties: IPropertySelection[] | undefined,
  showAllProperties: boolean
): boolean => {
  const hasActiveProperties =
    properties?.some((prop) => !prop.isPrevious) ?? false;
  return !showAllProperties || hasActiveProperties;
};
